import { ENGLISH_LEVEL_SHORT_OPTIONS } from 'domain/shared/core/constants';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES } from 'domain/shared/employee';
import { EXPIRATION_PERIOD_OPTIONS } from 'domain/shared/newVacancy';
import { POSITION_SLOT_WORKER_TYPE_LABELS, type PositionSlotListItem } from 'domain/shared/positionSlot';
import { EnumDropdown, NumberInput, TextInput, WysiwygInput } from 'framework/form';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import MatchingPositionsControl from './MatchingPositionsControl';

const FULL_WIDTH = { xs: 12, md: 12, xl: 12 };
const THIRD_WIDTH = { xs: 12, md: 4, xl: 4 };
const TRHREE_QUARTERS_WIDTH = { xs: 12, md: 8, xl: 8 };

const editorConfig = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'heading',
];

const fieldSx = {
  '& .floating-label-input, & .floating-label-baseInput, & .floating-label-dropdown': { mb: 0 },
};

const buildPositionLabel = (positionSlot: PositionSlotListItem) => {
  const title = [positionSlot.specializationLabel, positionSlot.technologyLabel, positionSlot.competencyLevel]
    .filter(Boolean)
    .join(' ');

  return [
    title,
    positionSlot.officesLabel,
    positionSlot.workRateType
      ? EMPLOYEE_CONTRACT_ASSIGMENT_TYPES[positionSlot.workRateType]
      : EMPLOYEE_CONTRACT_ASSIGMENT_TYPES.FULL_TIME,
    POSITION_SLOT_WORKER_TYPE_LABELS[positionSlot.workerType],
  ]
    .filter(Boolean)
    .join(', ');
};

const RichTextInput = ({ source }: { source: string }) => (
  <WysiwygInput source={source} label="" editorConfig={editorConfig} height={150} />
);

export const getCreatePositionFillRequestSections = (positionSlot: PositionSlotListItem): CreateEditFormSection[] => [
  {
    id: 'position-information',
    title: 'Position information',
    fields: [
      {
        id: 'positionSlotId',
        size: TRHREE_QUARTERS_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="positionSlotId"
            label="Position"
            options={{ [positionSlot.id]: buildPositionLabel(positionSlot) }}
            disabled
            isRequired
          />
        ),
      },
      {
        id: 'matchingPositionSlotIds',
        size: FULL_WIDTH,
        content: <MatchingPositionsControl positionSlot={positionSlot} />,
      },
    ],
  },
  {
    id: 'main-information',
    title: 'Main information',
    fields: [
      {
        id: 'expirationPeriodInMonth',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="expirationPeriodInMonth"
            label="Expiration period (Month)"
            options={EXPIRATION_PERIOD_OPTIONS}
            placeholder="Select period"
            isRequired
          />
        ),
      },
    ],
  },
  {
    id: 'responsibilities',
    title: 'Responsibilities*',
    fields: [
      {
        id: 'responsibilities-editor',
        size: FULL_WIDTH,
        content: <RichTextInput source="responsibilities" />,
      },
    ],
  },
  {
    id: 'requirements',
    title: 'Requirements*',
    fields: [
      {
        id: 'requiredExperienceInYears',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <NumberInput
            source="requiredExperience"
            label="Required experience"
            slotProps={{ htmlInput: { min: 0 } }}
            isRequired
          />
        ),
      },
      {
        id: 'englishLevel',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="englishLevel"
            label="English level"
            options={ENGLISH_LEVEL_SHORT_OPTIONS}
            placeholder="Select level"
            isRequired
          />
        ),
      },
      {
        id: 'requirements-editor',
        size: FULL_WIDTH,
        content: <RichTextInput source="requirements" />,
      },
    ],
  },
  {
    id: 'project-information',
    title: 'Project information',
    fields: [
      {
        id: 'projectName',
        size: THIRD_WIDTH,
        sx: fieldSx,
        content: <TextInput source="projectName" label="Project name" />,
      },
      {
        id: 'project-description-editor',
        size: FULL_WIDTH,
        content: <RichTextInput source="projectDescription" />,
      },
    ],
  },
  {
    id: 'notes',
    title: 'Notes',
    fields: [
      {
        id: 'notes-editor',
        size: FULL_WIDTH,
        content: <RichTextInput source="notes" />,
      },
    ],
  },
];
